import React, { useContext, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import { styled, Typography, useTheme } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import DashboardSendModalHeader from './components/DashboardSendModalHeader';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import WidgetTruncatedWarningContext from 'components/Contexts/WidgetTruncatedWarningContext';
import { DashboardT } from 'business/definitions';
import { useSelector } from 'store/hooks';
import DashboardSendEmailSteps from './components/DashboardSendEmailSteps';
import ScheduledDashboardEmailList from './ScheduledDashboardEmailList/ScheduledDashboardEmailList';
import {
    useDeleteScheduleEmailMutation,
    useGetScheduledDashboardEmails,
} from './hooks/scheduleDashboards';
import {
    DashboardSendEmailStepEnum,
    EmailRecurrence,
    FilterOption,
    ScheduledDashboardEmail,
} from './types';
import { getTodayDateString } from 'components/inputs/dateHelpers';

type Props = {
    dashboard: DashboardT;
    show: boolean;
    closeModal: () => void;
};

const StyDrawer = styled(Drawer, {
    name: 'StyDrawer',
})({
    '& .MuiPaper-root': {
        width: '32vw',
        padding: '1vh 1vw',
    },
});

const DashboardSendModal = ({ dashboard, show, closeModal }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { shouldShowWidgetTruncatedWarning } = useContext(
        WidgetTruncatedWarningContext,
    );
    const user = useSelector((state) => state.vision.user);
    const isSupportUser = useSelector((state) => state.vision.isSupportUser);

    const { data: userScheduledDashboards = [], isLoading } =
        useGetScheduledDashboardEmails(dashboard.key, 'self', isSupportUser);
    const { data: otherScheduledDashboards = [], isLoading: isOtherLoading } =
        useGetScheduledDashboardEmails(dashboard.key, 'other', isSupportUser);

    const deleteScheduleEmailMutation = useDeleteScheduleEmailMutation();

    const [step, setStep] = useState(DashboardSendEmailStepEnum.IDLE);

    const initialScheduledEmail: ScheduledDashboardEmail = {
        sender: user.email,
        senderId: user.id,
        shouldSendCopyToSender: true,
        recipients: [{ recipientId: user.id, internalEmail: user.email }],
        text: '',
        firstSentDate: getTodayDateString(),
        recurrence: EmailRecurrence.ONCE,
        filterOption: FilterOption.SENDER_FILTERS_SENDER_SCOPES,
    };
    const [email, setEmail] = useState(initialScheduledEmail);

    const resetForm = (scheduledEmail: ScheduledDashboardEmail) => {
        setEmail(scheduledEmail);
        setStep(DashboardSendEmailStepEnum.CONFIG_FORM);
    };

    return (
        <ErrorBoundary>
            <StyDrawer variant="persistent" anchor="right" open={show}>
                <DashboardSendModalHeader onClose={closeModal} />
                <Flex
                    sx={{ mt: theme.spacing(2) }}
                    flexDirection="column"
                    gap={theme.spacing(2)}
                >
                    <Typography>{t('sendDashboard.modalText')}</Typography>
                    {shouldShowWidgetTruncatedWarning && (
                        <Typography variant="body1SemiBold">
                            {t('widget.widgetTruncatedWarning')}
                        </Typography>
                    )}
                    <DashboardSendEmailSteps
                        dashboard={dashboard}
                        userEmail={user.email}
                        userId={user.id}
                        step={step}
                        setStep={setStep}
                        email={email}
                        setEmail={setEmail}
                        reset={() => resetForm(initialScheduledEmail)}
                    />
                    <ScheduledDashboardEmailList
                        emailList={userScheduledDashboards}
                        isLoading={isLoading}
                        onDeleteScheduleConfig={(sdeId) =>
                            deleteScheduleEmailMutation.mutate({ id: sdeId })
                        }
                        onEditScheduleConfig={(sdeId) => {
                            const scheduled = userScheduledDashboards.find(
                                (sde) =>
                                    sde.id?.toString() === sdeId.toString(),
                            );
                            if (scheduled === undefined) {
                                return;
                            }
                            resetForm(scheduled);
                        }}
                        listTitle={t(
                            'sendDashboard.scheduledDashboardEmailListTitle',
                        )}
                        noSchedule={t(
                            'sendDashboard.noScheduledDashboardEmail',
                        )}
                    />
                    {isSupportUser && (
                        <ScheduledDashboardEmailList
                            emailList={otherScheduledDashboards}
                            isLoading={isOtherLoading}
                            onEditScheduleConfig={(sdeId) => {
                                const scheduled = otherScheduledDashboards.find(
                                    (sde) =>
                                        sde.id?.toString() === sdeId.toString(),
                                );
                                if (scheduled === undefined) {
                                    return;
                                }
                                resetForm(scheduled);
                            }}
                            onDeleteScheduleConfig={(sdeId) =>
                                deleteScheduleEmailMutation.mutate({
                                    id: sdeId,
                                })
                            }
                            listTitle={t(
                                'sendDashboard.scheduledOtherDashboardEmailListTitle',
                            )}
                            noSchedule={t(
                                'sendDashboard.noScheduledDashboardEmail',
                            )}
                        />
                    )}
                </Flex>
            </StyDrawer>
        </ErrorBoundary>
    );
};

export default DashboardSendModal;
